"use strict"

import React, { useCallback, useEffect, useState } from "react"
import { Button } from "react-bootstrap"
import { max } from "d3-array"

import { logToServer } from "../../../../ErrorBoundary"
import { getObjectsList } from "../../../../Loaders"
import {
  isLTPM,
  id_accessor,
  getFormattedMessage,
} from "../../../../components/shared_components_registration"
import { stringifyDate, adjustDateValue, getTimestampDifference, getCurrentTime } from "../../../utils"
import TagOptionsWidget from "../../../graphing/resources/TagOptionsWidget"
import DashLoader from "../../../widgets/DashLoader"
import { rightPanelRegistration } from "../utils"
import { withGraphState } from "../../../config/GraphConfigProvider"
import DateInputCol from "./DateInputCol"
import FontSizeAdjuster from "./FontSizeAdjuster"
import MetricsTable from "./MetricsTable"

function PerformanceMetricsPanel({
  performance_metrics_panel, // From withGraphState
  runsAvailable,
  plotList = [],
  run,
  intl,
}) {
  const getColWidth = (idx) => {
    const col_map = { 0: 5, 1: 3, 2: 3, 3: 1 }
    return col_map[idx]
  }
  const getTextStr = (id, msg) => getFormattedMessage(id, msg, true, intl)
  const { headerRowStyle, headerColStyle, colValueStyle, noActiveRuns, errMsg } =
    rightPanelRegistration(isLTPM)

  // Both hold date values
  const [tsMin, setTSMin] = useState("")
  const [tsMax, setTSMax] = useState("")

  // Both indicate a date metric has changed but data not yet loaded - Controls style
  const [tsMinStatus, setTSMinStatus] = useState(false)
  const [tsMaxStatus, setTSMaxStatus] = useState(false)

  // Indicates if we should hit DB for metrics load for a given change
  const [tagLoad, setTagLoad] = useState(false)
  useEffect(() => {
    if (run) {
      // On run load/change set initial timestamp value(s) relative to observation_max
      if (run.observation_max || run.inoculated_on) {
        const refTS = run.observation_max || getCurrentTime()
        setTSMax(stringifyDate(refTS))
        tsMin === "" && setTSMin(stringifyDate(adjustDateValue(refTS, "hour", 6, false))) // Only set for the first run. Leave alone otherwise
        setTagLoad(true)
      }
      setErr(!!run.err)
    }
  }, [run])

  const [loading, setLoading] = useState(runsAvailable)
  const [values, setValues] = useState({})
  const [err, setErr] = useState(false)
  const [metrics, setMetrics] = useState([])
  const updateMetrics = useCallback((metricsList, loadTags) => {
    setMetrics(metricsList)
    setTagLoad(loadTags)
  }, [])

  useEffect(() => {
    updateMetrics(performance_metrics_panel, true)
  }, [performance_metrics_panel])

  const onMetricLoad = useCallback((info, err) => {
    setLoading(false)
    setTagLoad(false)
    setTSMinStatus(false)
    setTSMaxStatus(false)
    setValues(info)
    setErr(err)
  }, [])

  const loadTagMeans = () => {
    if (run?.[id_accessor] && metrics.length > 0 && tsMin && tsMax) {
      let hours = ((getTimestampDifference(tsMin, tsMax) || {})["hours"] || 0).toFixed(5)
      if (hours > 8760) {
        hours = 8760 // Hard set boundary of ten years of difference (shouldn't realistically come into play). TODO - let user see that they have ten year range limit
      }
      const ts = max([tsMin, tsMax])
      const encodedDate = encodeURIComponent(stringifyDate(ts, true, true))
      const url = `/warehouse/${isLTPM ? "runs" : "runreactors"}/${run[id_accessor]}/recent_mean/?tag=${metrics
        .map((metric) => metric.tag)
        .join()}&units=${metrics.map((metric) => metric.units).join()}&hours=${hours}&maxtimes=${encodedDate}`

      setErr(false)
      setLoading(true)
      getObjectsList(url)
        .then((tag_means) => {
          onMetricLoad(tag_means.length === 1 ? tag_means[0]["mean"] : {}, false)
        })
        .catch((err) => {
          console.error(err)
          logToServer(err)
          onMetricLoad({}, err)
        })
    }
  }

  useEffect(() => {
    tagLoad && loadTagMeans()
  }, [tagLoad, metrics, tsMin, tsMax])

  const updateTagList = useCallback(
    (selections) => {
      // Handle selections of new tags
      let modMetrics = Object.assign([], metrics)
      let metricsUpdated = false
      selections.forEach((sel) => {
        const tag = sel?.observations ? sel.observations[0] : null
        if (tag && !modMetrics.map((m) => m.tag).includes(tag.tag)) {
          // Only bother adding tags if not already in list
          metricsUpdated = true
          modMetrics.unshift({ tag: tag.tag || "", units: tag.units || "" })
        }
      })
      metricsUpdated && updateMetrics(modMetrics, true)
    },
    [metrics],
  )

  const [fontPercent, setFontPercent] = useState(80)

  return (
    <div className={"container-fluid"} style={{ height: "100%", position: "relative" }}>
      <div style={{ padding: "0", position: "absolute", bottom: "5px", left: "0", zIndex: "2" }}>
        <TagOptionsWidget
          plotList={plotList}
          updateTagList={updateTagList}
          disabled={loading || !runsAvailable}
          intl={intl}
          placement={"left"}
          siteReactors={[{ site: run?.site, reactor: run?.reactor }]}
        />
      </div>
      <div style={{ padding: "0", position: "absolute", bottom: "5px", right: "0", zIndex: "2" }}>
        <FontSizeAdjuster
          fontPercent={fontPercent}
          setFontPercent={setFontPercent}
          disabled={loading || !runsAvailable}
          intl={intl}
        />
      </div>
      <div className={"row"} style={{ marginTop: "3px", marginBottom: "3px", alignItems: "center" }}>
        <DateInputCol
          fontPct={fontPercent}
          disabled={loading || !runsAvailable}
          ts={tsMin}
          setTs={(val) => setTSMin(stringifyDate(val))} // Stringify for consistent/accurate gte or lte ts comparison
          status={tsMinStatus}
          setStatus={setTSMinStatus}
        />
        <div className={"col-1 text-center"} style={{ padding: "0" }}>
          :
        </div>
        <DateInputCol
          fontPct={fontPercent}
          disabled={loading || !runsAvailable}
          ts={tsMax}
          setTs={(val) => setTSMax(stringifyDate(val))} // Stringify for consistent/accurate gte or lte ts comparison
          status={tsMaxStatus}
          setStatus={setTSMaxStatus}
        />
        <div className={"col-1"} style={{ padding: "0" }}>
          <Button
            variant={"outline-primary"}
            className={"btn btn-sm"}
            style={{ width: "100%" }}
            disabled={loading || !tsMin || !tsMax || !runsAvailable}
            title={getTextStr("general.submitButton", "Submit")}
            onClick={(e) => {
              e.preventDefault()
              loadTagMeans()
            }}
          >
            <span className={"fa fa-search"} />
          </Button>
        </div>
      </div>
      <div className={"row"} style={headerRowStyle}>
        {[
          getTextStr("general.metric", "Metric"),
          getTextStr("general.value", "Value"),
          getTextStr("general.units", "Units"),
          "",
        ].map((header, idx) => (
          <div className={`col-${getColWidth(idx)}`} key={`metric_header_${idx}`} style={headerColStyle}>
            <p className={"text-center"} style={{ margin: "0px", fontSize: `${fontPercent}%` }}>
              {header}
            </p>
          </div>
        ))}
      </div>
      {err ? (
        <p style={{ marginTop: "10px", marginBottom: "20px" }} className={"error-message"}>
          {errMsg(intl)}
        </p>
      ) : (
        <div
          style={{
            height: "calc(100% - 60px)",
            overflowY: "auto",
            overflowX: "hidden",
            marginLeft: "-15px",
            marginRight: "-15px",
            position: "relative",
          }}
        >
          {!runsAvailable ? (
            <p style={{ marginTop: "25px", textAlign: "center" }}>{noActiveRuns(intl)}</p>
          ) : loading ? (
            <div className={"dashed-black-border"} style={{ height: "100%" }}>
              <DashLoader />
            </div>
          ) : (
            Object.keys(values).length > 0 && (
              <MetricsTable
                metrics={metrics}
                values={values}
                plotList={plotList}
                fontPercent={fontPercent}
                updateMetrics={updateMetrics}
                getColWidth={getColWidth}
                colValueStyle={colValueStyle}
              />
            )
          )}
        </div>
      )}
    </div>
  )
}

export default withGraphState(PerformanceMetricsPanel)
